import {Dashboard} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import BackEndService from "../../../api/BackEndService";
import CustomerService from "../../../api/CustomerService";
import UserService from "../../../api/UserService";
import {LastServices} from "../../../components/LastServices/LastServices";
import {SubMenuButton} from "../../../components/SubMenuButton/SubMenuButton";
import {useScreenSize} from "../../../hooks/useScreenSize";
import {AppLayout} from "../../../layout/AppLayout/AppLayout";
import {generateUUIDFromID} from "../../../services/UUIDGeneratorService";
import {RootState} from "../../../store";
import {
  PropertyInfo,
  ROLES,
  userSliceActions,
} from "../../../store/user-slice";
import styles from "../../../styles/customer-dashboard.module.css";
import {Property} from "../../../types/property";

export type ButtonType = "PastCustomerVisits" | "UpcomingCustomerVisits";

const SUB_MENU_BUTTONS: ButtonType[] = [
  // "UpcomingCustomerVisits",  comment this tab for now
  "PastCustomerVisits",
];

const CustomerDashboard = () => {
  const dispatch = useDispatch();
  const {isDesktop} = useScreenSize();
  const {t} = useTranslation();
  const [currentTab, setCurrentTab] =
    useState<ButtonType>("PastCustomerVisits");
  const [loading, setLoading] = useState(true);
  const selectedPropertyId = useSelector(
    (state: RootState) => state.user.selectedPropertyId
  );

  const handleChangeTab = (tab: ButtonType) => {
    setCurrentTab(tab);
  };

  const fetchCustomerProperties = async (customerId: string, sevdeskId: string | null) => {
    let {data} = await CustomerService.GetCustomerProperties(customerId);
    const sevdeskProperties = sevdeskId ? await BackEndService.GetObjects(sevdeskId) : null;

    if (!data || data.length === 0) {
      data = [];
    }

    // Update database properties attributes with sevdesk attributes if they exist
    const updatedProperties = data.map(async (property: Property) => {
      const sevdeskProperty = property.isSevdeskProperty ? sevdeskProperties?.data?.find(prop => generateUUIDFromID(prop.id) === property.id) : null;
      const object: any = sevdeskProperty ? {...sevdeskProperty, postcode: sevdeskProperty.zip, isSevdeskProperty: property.isSevdeskProperty, customerReferenceNumber: property.customerReferenceNumber, id: property.id} : property;

      return {
        id: object.id,
        street: object.street,
        postcode: object.postcode,
        city: object.city,
        name1: object.name1,
        isSevdeskProperty: object.isSevdeskProperty,
        customerReferenceNumber: object.customerReferenceNumber,
      }
    })

    if (sevdeskProperties && sevdeskProperties.data) {
      const newSevdeskProperties = sevdeskProperties.data.filter(
        (sevdeskProperty: any) =>
          !data.some((property: Property) => generateUUIDFromID(sevdeskProperty.id) === property.id)
      ).map((sevdeskProperty: any) => ({
        id: generateUUIDFromID(sevdeskProperty.id),
        street: sevdeskProperty.street,
        postcode: sevdeskProperty.zip,
        city: sevdeskProperty.city,
        name1: null,
        isSevdeskProperty: 1,
        customerReferenceNumber: sevdeskProperty.id,
      }));

      updatedProperties.push(...newSevdeskProperties);
    }

    return Promise.all(updatedProperties);
  };

  useEffect(() => {
    UserService.GetCurrentUser().then(({data}) => {
      dispatch(userSliceActions.setCurrentUser(data));

      const isObjekbetreuer = data.roles?.includes(ROLES.PROPERTY_MANAGER);
      const isKundeAdmin = data.roles?.includes(ROLES.CUSTOMER_ADMIN);

      if (!data.customerId) {
        setLoading(false);
        return;
      }

      fetchCustomerProperties(data.customerId, data.sevdeskId ?? null).then(
        (filteredProperties: PropertyInfo[]) => {
          let propertiesToSave = filteredProperties;

          if (isObjekbetreuer && data?.assignedProperties && !isKundeAdmin) {
            propertiesToSave = filteredProperties.filter((property) =>
              data.assignedProperties?.some(
                (assignedProperty: string) => assignedProperty === property.id
              )
            );

          }

          dispatch(userSliceActions.setCustomerProperties(propertiesToSave));

          if (selectedPropertyId === "" || selectedPropertyId == null) {
            dispatch(
              userSliceActions.setSelectedPropertyId(propertiesToSave[0]?.id)
            );
            dispatch(userSliceActions.setSelectedProperty(propertiesToSave[0]));
          }
          setLoading(false);
        }
      );
    });
  }, [dispatch, selectedPropertyId]);

  return (
    <AppLayout>
      <div id="scrollable-wrapper" className={styles["scrollable-wrapper"]}>
        {isDesktop && (
          <p className={styles["dashboard-title"]}>
            <Dashboard /> {t("LastServices")}
          </p>
        )}
        {!loading && (
          <div className={styles["tabs"]}>
            <div className={styles["buttons"]}>
              {SUB_MENU_BUTTONS.map((button, i) => (
                <SubMenuButton
                  currentTab={currentTab}
                  value={button}
                  onSetTab={() => handleChangeTab(button)}
                  key={i}
                />
              ))}
            </div>

            {currentTab === "PastCustomerVisits" && <LastServices />}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default CustomerDashboard;
